// eslint-disable-next-line import/no-extraneous-dependencies
import { compare } from "compare-versions";
import { useEffect, useState, useCallback, ReactNode } from "react";

import packageJson from "../../package.json";

interface ICacheBusterWrapperProps {
  children: ReactNode;
  isEnabled?: boolean;
  isVerboseMode?: boolean;
  metaFileDirectory?: string;
  reloadOnDowngrade?: boolean;
  onCacheClear?: (refreshFunction: () => void) => void;
}

interface ICacheStatus {
  loading: boolean;
  isLatestVersion: boolean;
  metaVersion?: string;
}

// Guards against reload loops: if the served bundle stays stale (e.g. a CDN edge
// still caching the old index.html or meta.json during a deploy window), we would
// otherwise reload forever. Allow at most one reload per detected meta version.
const RELOAD_GUARD_STORAGE_KEY = "CACHE_BUSTER_RELOADED_FOR_VERSION";

/**
 * Custom CacheBuster component that properly handles meta.json caching
 * by adding cache-busting query parameters to avoid infinite reload loops
 */
export function CacheBusterWrapper({
  children,
  isEnabled = false,
  isVerboseMode = false,
  metaFileDirectory = ".",
  reloadOnDowngrade = false,
  onCacheClear,
}: ICacheBusterWrapperProps) {
  const [cacheStatus, setCacheStatus] = useState<ICacheStatus>({
    loading: true,
    isLatestVersion: false,
  });

  const log = useCallback(
    (message: string, isError = false) => {
      if (isVerboseMode) {
        if (isError) {
          console.error(message);
        } else {
          console.log(message);
        }
      }
    },
    [isVerboseMode]
  );

  const getMetaFileDirectory = useCallback(() => {
    return !metaFileDirectory || metaFileDirectory === "."
      ? ""
      : metaFileDirectory;
  }, [metaFileDirectory]);

  const isThereNewVersion = useCallback(
    (metaVersion: string, currentVersion: string) => {
      if (reloadOnDowngrade) {
        return !compare(metaVersion, currentVersion, "=");
      }
      return compare(metaVersion, currentVersion, ">");
    },
    [reloadOnDowngrade]
  );

  const checkCacheStatus = useCallback(async () => {
    try {
      // Add cache-busting timestamp to meta.json request to prevent infinite loops
      const cacheBuster = `?t=${new Date().getTime()}`;
      const metaUrl = `${getMetaFileDirectory()}/meta.json${cacheBuster}`;

      log(`Fetching meta file from: ${metaUrl}`);

      const response = await fetch(metaUrl, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const { version: metaVersion } = await response.json();

      const shouldForceRefresh = isThereNewVersion(
        metaVersion,
        packageJson.version
      );

      if (shouldForceRefresh) {
        if (
          window.sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY) ===
          metaVersion
        ) {
          log(
            `Already reloaded once for v${metaVersion} but the served bundle is still v${packageJson.version}. Skipping reload to avoid a loop.`,
            true
          );
          setCacheStatus({
            loading: false,
            isLatestVersion: true,
          });
          return;
        }
        log(
          `There is a new version (v${metaVersion}). Current version: v${packageJson.version}. Should force refresh.`
        );
        setCacheStatus({
          loading: false,
          isLatestVersion: false,
          metaVersion,
        });
      } else {
        log("There is no new version. No cache refresh needed.");
        setCacheStatus({
          loading: false,
          isLatestVersion: true,
        });
      }
    } catch (error) {
      log("An error occurred while checking cache status.", true);
      log(String(error), true);
      // On error, don't block the app - just proceed
      setCacheStatus({
        loading: false,
        isLatestVersion: true,
      });
    }
  }, [getMetaFileDirectory, isThereNewVersion, log]);

  const refreshCacheAndReload = useCallback(async () => {
    try {
      if (cacheStatus.metaVersion) {
        window.sessionStorage.setItem(
          RELOAD_GUARD_STORAGE_KEY,
          cacheStatus.metaVersion
        );
      }

      // Clear all caches if available
      if (window?.caches) {
        const cacheNames = await window.caches.keys();
        const cacheDeletionPromises = cacheNames.map((name) =>
          window.caches.delete(name)
        );
        await Promise.all(cacheDeletionPromises);
        log("The cache has been deleted.");
      }

      // Force hard reload
      window.location.reload();
    } catch (error) {
      log("An error occurred while deleting the cache.", true);
      log(String(error), true);
    }
  }, [log, cacheStatus.metaVersion]);

  useEffect(() => {
    if (isEnabled) {
      checkCacheStatus();
    } else {
      log("Cache Buster is disabled.");
      setCacheStatus({
        loading: false,
        isLatestVersion: true,
      });
    }
  }, [isEnabled, checkCacheStatus, log]);

  // If disabled, just render children
  if (!isEnabled) {
    return children;
  }

  // While loading, show nothing (or could show a loading component)
  if (cacheStatus.loading) {
    return null;
  }

  // If not latest version, trigger cache clear and reload
  if (!cacheStatus.loading && !cacheStatus.isLatestVersion) {
    if (onCacheClear) {
      onCacheClear(refreshCacheAndReload);
    } else {
      refreshCacheAndReload();
    }
    return null;
  }

  // Render children if we have the latest version
  return children;
}
